import { configWithUserToken } from "../../services/config";
import {
  DineInLineStatus, IDineInBill, IDineInFloor, IDineInHistoryPage, IDineInNewLine, IDineInSession,
} from "../models/IDineIn";

/** Salon görünümü: hizmetteki masalar, Available/Busy, açık oturumun kodu ve toplamı. */
export const getDineInFloor = (restaurantId: string) =>
  configWithUserToken.get<IDineInFloor>(`DineIn/floor/${restaurantId}`);

/** Masayı Busy'ye çeker ve sipariş kodu üretir. İstekte yalnızca masa gider. */
export const openDineInSession = (tableId: string) =>
  configWithUserToken.post<IDineInSession>("DineIn/sessions", { tableId });

/** Masayı ödemesiz boşaltır (oturum Cancelled). Satırı varsa reddedilir. */
export const releaseDineInSession = (orderCode: string) =>
  configWithUserToken.post<IDineInSession>(`DineIn/sessions/${orderCode}/release`);

/**
 * Ödendi. `expectedTotalAmount` ekranda görünen toplam olmalı; sunucuyla uyuşmazsa reddedilir.
 * `paymentMethodId` **zorunlu** ve salon yalnızca nakit/kart kabul eder (PaymentMethodNotAllowed).
 */
export const payDineInSession = (orderCode: string, expectedTotalAmount: number, paymentMethodId: string) =>
  configWithUserToken.post<IDineInSession>("DineIn/sessions/paid", {
    orderCode, expectedTotalAmount, paymentMethodId,
  });

/** Bir tur yemek ekler (önceki satırlar silinmez). Tek istekte en çok 100 satır. */
export const addDineInOrderLines = (orderCode: string, lines: IDineInNewLine[]) =>
  configWithUserToken.post<IDineInBill>("DineIn/order-lines", { orderCode, lines });

/** Satırın adedini / notunu değiştirir. Not yama değil değiştirmedir: boş gelirse not silinir. */
export const updateDineInOrderLine = (lineId: string, quantity: number, foodOrderNote?: string) =>
  configWithUserToken.put<IDineInBill>("DineIn/order-lines", { lineId, quantity, foodOrderNote });

/**
 * Satır(lar)ın durumunu ilerletir (yalnız ileri: Preparing → Ready → Served, Preparing → Served da olur).
 * Aynı hesaptan 1–100 satır; ya hep ya hiç. Zaten hedef durumdaki satır no-op. Yanıt güncel hesap.
 */
export const updateDineInOrderLineStatus = (lineIds: string[], status: DineInLineStatus) =>
  configWithUserToken.put<IDineInBill>("DineIn/order-lines/status", { lineIds, status });

export const deleteDineInOrderLine = (lineId: string) =>
  configWithUserToken.delete<IDineInBill>(`DineIn/order-lines/${lineId}`);

/** Masadaki açık hesap. Masa boşsa 404 NoOpenSession. */
export const getDineInBillByTable = (tableId: string) =>
  configWithUserToken.get<IDineInBill>(`DineIn/bill/by-table/${tableId}`);

/** Kod ile hesap — ödenmiş/boşaltılmış oturumlar da gelir (salt okunur). */
export const getDineInBillByCode = (orderCode: string) =>
  configWithUserToken.get<IDineInBill>(`DineIn/bill/by-code/${orderCode}`);

export interface IDineInHistoryParams {
  tableId: string;
  fromUtc?: string;
  toUtc?: string;
  page?: number;
  pageSize?: number;
}

/** Masa bazlı geçmiş. Aralık gönderilmezse sunucu son 1 ayı uygular ve pencereyi yanıtta döner. */
export const getDineInHistory = (params: IDineInHistoryParams) => {
  const p = new URLSearchParams();
  p.append("tableId", params.tableId);
  if (params.fromUtc) p.append("fromUtc", params.fromUtc);
  if (params.toUtc) p.append("toUtc", params.toUtc);
  p.append("page", String(params.page ?? 1));
  p.append("pageSize", String(params.pageSize ?? 20));
  return configWithUserToken.get<IDineInHistoryPage>(`DineIn/history?${p.toString()}`);
};

export interface IDineInSalonHistoryParams {
  restaurantId: string;
  fromUtc?: string;
  toUtc?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Bütün salonun geçmişi (restoran bazlı). Sıralama yeniden eskiye, açılış zamanına göre;
 * hâlâ açık oturumlar da gelir. Aralık en çok 92 gün (aşılırsa InvalidHistoryRange).
 */
export const getDineInSalonHistory = (params: IDineInSalonHistoryParams) => {
  const p = new URLSearchParams();
  p.append("restourantId", params.restaurantId);
  if (params.fromUtc) p.append("fromUtc", params.fromUtc);
  if (params.toUtc) p.append("toUtc", params.toUtc);
  p.append("page", String(params.page ?? 1));
  p.append("pageSize", String(params.pageSize ?? 20));
  return configWithUserToken.get<IDineInHistoryPage>(`DineIn/history?${p.toString()}`);
};

/** Salon hata gövdesi dizidir: [{ code, description }] — dal code üzerine yazılmalı. */
export function dineInErrorCode(err: any): string | undefined {
  const data = err?.response?.data;
  return Array.isArray(data) ? data[0]?.code : undefined;
}

export function dineInErrorDescription(err: any): string | undefined {
  const data = err?.response?.data;
  return Array.isArray(data) ? data[0]?.description : undefined;
}
